import React, { useCallback, useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import moment from 'moment-timezone'
import ContentHeader from './ContentHeader'
import {
  useI18n,
  useRehabSelectedThemes,
  useUserProfile,
} from 'contexts/AuthContext'
import calendarIcon from './img/ic-calendar-event.svg'
import { toDate } from 'utils/basic'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getSkuThemesPromise, updateUserPromise } from 'apis/FitOnCareAPI'
import { logBrazeEvent, setBrazeUserAttribute } from 'utils/brazeInit'
import ConfirmedAppointmentContent from 'components/ConfirmedAppointmentContent/index'
import { ScreenKey } from './util'
import { useSkuCondition } from 'hooks/useSkuCondiction'
import { useApplyNewPatientTag } from 'hooks/useApplyTags'
import _ from 'lodash'

const ConfirmedAppointment = (props) => {
  const {
    bookedAppointment,
    appointmentsCount,
    promo,
    scheduleLanguage,
    onBack,
    sku = 'rd',
    gender,
  } = props
  const { rehabSelectedThemes } = useRehabSelectedThemes()
  const { skuCondition } = useUserProfile()
  const { getNewSkuCondition } = useSkuCondition()
  const { i18n } = useI18n()
  const { applyNewPatientTag } = useApplyNewPatientTag()

  useEffect(() => {
    if (appointmentsCount === 0 && bookedAppointment && sku === 'pt') {
      applyNewPatientTag()
      logBrazeEvent('Schedule Appointment End')
    }
  }, [bookedAppointment, appointmentsCount, sku])

  const { theme } = useMemo(() => {
    return {
      theme: rehabSelectedThemes?.map((theme) => theme.name),
    }
  }, [rehabSelectedThemes])

  useEffect(() => {
    if (!_.isEmpty(rehabSelectedThemes) && !_.isEmpty(skuCondition)) {
      switch (sku) {
        case 'msk':
          getSkuThemesPromise({ gender, sku: 'pt' }).then((resp) => {
            const themes = resp?.themes || []
            const themeArray = themes.filter((theme) => {
              return rehabSelectedThemes.find(
                (selectedTheme) => selectedTheme.name === theme.name
              )
            })
            if (!_.isEmpty(themeArray)) {
              const themeIds = themeArray.map((theme) => theme.id)
              const newSkuCondition = getNewSkuCondition({
                skuCondition,
                themeIds,
                sku: 'pt',
              })
              updateUserPromise({
                skuCondition: newSkuCondition,
              })
            }
          })
          break
        default:
          break
      }
    }
  }, [rehabSelectedThemes, sku, skuCondition, gender])

  useEffect(() => {
    const startDate = toDate(bookedAppointment.start)
    const params = {
      Practitioner: bookedAppointment.provider?.full_name,
      Date: format(startDate, `yyyy-MM-dd`),
      Time: format(startDate, `h:mm a`),
      'Day of Week': startDate.getDay() + 1,
      language: getLocalLanguage(),
      sku,
    }
    switch (sku) {
      case 'wlc': {
        logAmplitudeEvent('Screen View: Onboarding - Schedule - Confirmation', {
          ...params,
          sku: 'wlc',
          'Appointment Language': scheduleLanguage?.name || 'English',
        })

        updateUserPromise({ skuStatus: { sku: 'rd', status: 2 }, activate: 1 })
        break
      }
      case 'msk': {
        logAmplitudeEvent('Screen View: Onboarding - Schedule - Confirmation', {
          ...params,
          sku: 'msk',
          theme: theme.join(', '),
          'Appointment Language': scheduleLanguage?.name || 'English',
        })

        updateUserPromise({
          skuStatus: {
            sku: 'pt',
            status: 2,
          },
        })
        break
      }
      case 'pt': {
        logAmplitudeEvent('Screen View: Onboarding - Schedule - Confirmation', {
          ...params,
          sku: 'pt',
          theme: theme.join(', '),
          'Appointment Language': scheduleLanguage?.name || 'English',
        })

        logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
          sku: sku,
          language: getLocalLanguage(),
          theme: theme.join(', '),
        })
        logBrazeEvent(`User Approved - ${sku}`)

        updateUserPromise({
          skuStatus: {
            sku: 'pt',
            status: 2,
          },
        })
        break
      }
      default:
        logAmplitudeEvent('Screen View: Onboarding - Schedule - Confirmation', {
          ...params,
          'Chosen Language': scheduleLanguage?.name || 'English',
        })

        logAmplitudeEvent('Screen View: Onboarding - Signup Success', {
          sku,
          ...(promo && { 'Promo Code': promo }),
          language: getLocalLanguage(),
        })

        updateUserPromise({ skuStatus: { sku: 'rd', status: 2 }, activate: 1 })
        break
    }

    if (appointmentsCount === 0) {
      setBrazeUserAttribute(
        'First Appointment',
        moment(toDate(bookedAppointment.start)).format()
      )
    }
  }, [appointmentsCount, bookedAppointment, scheduleLanguage, sku])

  const hideBack = useMemo(() => {
    return sku === 'rd' || !sku
  }, [sku])

  const showNextButton = useMemo(() => {
    return ['wlc', 'msk'].includes(sku)
  }, [sku])

  const onClickNext = useCallback(() => {
    switch (sku) {
      case 'wlc': {
        props.setCurrentKey(ScreenKey.SKU_CONGRATULATIONS)
        break
      }
      case 'msk': {
        props.setCurrentKey(ScreenKey.REHAB_CONGRATULATIONS)
        break
      }
      default:
        window.location.href = 'https://fitoncare.com/'
        break
    }
  }, [sku, props])

  return (
    <div className='info-sec'>
      <ContentHeader {...props} hideBack={hideBack} onBack={onBack} />
      <div className='info-content'>
        <ConfirmedAppointmentContent
          title={i18n('Appointment confirmed!')}
          subtitle={i18n('A confirmation has been sent to your email.')}
          bookedAppointment={bookedAppointment}
          icon={calendarIcon}
          showNextButton={showNextButton}
          onClickNext={onClickNext}
        />
      </div>
    </div>
  )
}

export default ConfirmedAppointment
